import { Add } from "@mui/icons-material";
import { Box, Button, Card, styled, useTheme } from "@mui/material";
import FlexBetween from "components/flexbox/FlexBetween";
import FlexBox from "components/flexbox/FlexBox";
import SearchInput from "components/input-fields/SearchInput";
import { H6 } from "components/Typography";
import { useEffect, useState, useCallback } from "react";
import { useTranslation } from "react-i18next";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import AddEventModal from "page-sections/Event/AddEventModal";
import EventTable from "page-sections/Event/EventTable";
import apiService from "Network/apiService";
import { API_ENDPOINTS } from "Network/apiEndpoints";
import { formatDate } from "utils/utils";
import { usePageTitle } from "contexts/PageTitleContext";

const ButtonWrapper = styled(FlexBox)(({ theme }) => ({
    [theme.breakpoints.down(500)]: {
        marginTop: 10,
        width: "100%",
        flexDirection: "column-reverse",
        "& > .MuiBox-root": {
            width: "100%",
            margin: "10px 0",
            alignItems: "center",
            flexDirection: "column"
        },
        "& .MuiButton-root": {
            minWidth: "100%"
        }
    }
}));

const DatePickerWrapper = styled(Box)(({ theme }) => ({
    display: "flex",
    alignItems: "center",
    gap: "10px",
    "& .react-datepicker-wrapper": {
        width: "130px"
    },
    "& .react-datepicker__input-container input": {
        padding: "8px 12px",
        border: `1px solid ${theme.palette.divider}`,
        borderRadius: theme.shape.borderRadius,
        width: "100%",
        fontSize: "0.875rem",
        color: theme.palette.text.primary,
        backgroundColor: theme.palette.background.paper,
        transition: theme.transitions.create(['border-color', 'box-shadow']),
        '&:hover': {
            borderColor: theme.palette.primary.main
        },
        '&:focus': {
            borderColor: theme.palette.primary.main,
            outline: 'none',
            boxShadow: `0 0 0 2px ${theme.palette.primary.main}25`
        }
    },
    "& .react-datepicker": {
        backgroundColor: theme.palette.background.paper,
        border: `1px solid ${theme.palette.divider}`,
        borderRadius: theme.shape.borderRadius,
        fontFamily: theme.typography.fontFamily,
        "& .react-datepicker__header": {
            backgroundColor: theme.palette.background.default,
            borderBottom: `1px solid ${theme.palette.divider}`,
            "& .react-datepicker__current-month": {
                color: theme.palette.text.primary,
                fontSize: "0.875rem",
                fontWeight: theme.typography.fontWeightMedium
            },
            "& .react-datepicker__day-name": {
                color: theme.palette.text.secondary
            }
        },
        "& .react-datepicker__day": {
            color: theme.palette.text.primary,
            "&:hover": {
                backgroundColor: theme.palette.action.hover
            },
            "&.react-datepicker__day--selected": {
                backgroundColor: theme.palette.primary.main,
                color: theme.palette.primary.contrastText,
                "&:hover": {
                    backgroundColor: theme.palette.primary.dark
                }
            },
            "&.react-datepicker__day--keyboard-selected": {
                backgroundColor: theme.palette.primary.main,
                color: theme.palette.primary.contrastText
            },
            "&.react-datepicker__day--disabled": {
                color: theme.palette.text.disabled
            }
        },
        "& .react-datepicker__triangle": {
            display: "none"
        }
    }
}));

const Events = () => {
    const theme = useTheme();
    const { t } = useTranslation();
    const [events, setEvents] = useState([]);//change initail value in use state
    const [tableData, setTableData] = useState([]);
    const [hasFilter, setHasFilter] = useState("");
    const [clearFilter, setClearFilter] = useState("");
    const [openModal, setOpenModal] = useState(false);

    const [searchValue, setSearchValue] = useState("");
    const [filteredItem, setFilteredItem] = useState(tableData);

    const { setPageTitle } = usePageTitle();

    useEffect(() => {
        setPageTitle('Event Management & Scheduling');
    }, [setPageTitle]);

    const formatDateToAPI = (date) => {
        if (!date) return null;
        const isoString = new Date(date).toISOString();
        return isoString.slice(0, 10); // Returns "YYYY-MM-DD"
    };

    // Function to get the first and last day of the current month
    const getCurrentMonthRange = () => {
        const now = new Date();
        const firstDay = new Date(now.getFullYear(), now.getMonth(), 1);
        const lastDay = new Date(now.getFullYear(), now.getMonth() + 1, 0);
        return { firstDay, lastDay };
    };
      // Initialize start and end dates to the current month's range
      const { firstDay: initialStartDate, lastDay: initialEndDate } = getCurrentMonthRange();
      const [startDate, setStartDate] = useState(initialStartDate);
      const [endDate, setEndDate] = useState(initialEndDate);

    const fetchEvents = useCallback(async (startDay, endDay) => {
        try {
            const formattedStartDay = formatDateToAPI(startDay);
            const formattedEndDay = formatDateToAPI(endDay);
            const response = await apiService.get(`${API_ENDPOINTS.GET_event}?start_day=${formattedStartDay}&end_day=${formattedEndDay}`);

            if (response?.success && response?.data) {
                const formattedEvents = response.data.map(event => ({
                    ...event,
                    startDate: event.start_day,
                    endDate: event.end_day
                }));
                setEvents(formattedEvents);
            } else {
                console.error("Failed to fetch events:", response?.message);
                setEvents([]); // Ensure events is an empty array in case of failure
            }
        } catch (error) {
            console.error("API error:", error);
            setEvents([]); // Ensure events is an empty array in case of error
        }
    }, []);

    useEffect(() => {
        if (startDate && endDate) {
            fetchEvents(startDate, endDate);
        } else {
            // Optionally, fetch all events if no date range is selected
            fetchEvents(null, null); // Or setEvents([]) to clear the table
        }
    }, [startDate, endDate, fetchEvents]);

    const handleClearFilter = () => {
        setClearFilter("...");
        setStartDate(null);
        setEndDate(null);
        setTimeout(() => {
            setClearFilter("");
        }, 50);
    };

    const closeModelAndCallAPi = () => {
        setOpenModal(false);
        // Use current date range for refresh
        fetchEvents(startDate, endDate);
    };

    const handleDeleteSuccess = useCallback(() => {
        // Refresh events with current date range
        fetchEvents(startDate, endDate);
    }, [fetchEvents, startDate, endDate]);

    return (
        <Box pt={2} pb={4}>
            <FlexBetween flexWrap="wrap">
                <FlexBox alignItems="center" gap={2}>
                    <SearchInput
                        bordered={false}
                        placeholder="Find Event"
                        onChange={e => setSearchValue(e.target.value)}
                        value={searchValue}
                    />
                    <DatePickerWrapper>
                        <DatePicker
                            selected={startDate}
                            onChange={date => setStartDate(date)}
                            selectsStart
                            startDate={startDate}
                            endDate={endDate}
                            placeholderText="Start Date"
                            dateFormat="MM/dd/yyyy"
                        />
                        <DatePicker
                            selected={endDate}
                            onChange={date => setEndDate(date)}
                            selectsEnd
                            startDate={startDate}
                            endDate={endDate}
                            minDate={startDate}
                            placeholderText="End Date"
                            dateFormat="MM/dd/yyyy"
                        />
                    </DatePickerWrapper>
                </FlexBox>

                <ButtonWrapper alignItems="center">
                    {/* {(!!hasFilter || startDate || endDate) &&
                        <FlexBox alignItems="center" mr={2}>
                            <Button
                                size="small"
                                color="error"
                                variant="contained"
                                sx={{
                                    color: "common.white"
                                }}
                                onClick={handleClearFilter}
                            >
                                Clear filter
                            </Button>
                        </FlexBox>
                    } */}

                    <Button
                        variant="contained"
                        size="small"
                        endIcon={<Add />}
                        onClick={() => setOpenModal(true)}
                    >
                        {t("Add Event")}
                    </Button>
                </ButtonWrapper>
            </FlexBetween>

            <AddEventModal
                open={openModal}
                onClose={(shouldRefresh) => {
                    setOpenModal(false);
                    if (shouldRefresh) {
                        fetchEvents(startDate, endDate);
                    }
                }}
            />

            <Card sx={{
                marginTop: 3
            }}>
                <EventTable
                    events={events}
                    searchQuery={searchValue}
                    clearFilter={clearFilter}
                    onFilterChange={filters => setHasFilter(filters.length)}
                    onDeleteSuccess={handleDeleteSuccess}
                />
            </Card>
        </Box>
    );
};

export default Events;
